// Phase 9.0 — Lifted Training Experience (slideshow mode)

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Save, CheckCircle2, Award, Maximize2, X,
  BookOpen, FileText, Loader2, Lock, Menu,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

function pct(n) { return `${Math.round(n)}%`; }

export default function TrainingExperience() {
  const [params, setParams] = useSearchParams();
  const [curriculum, setCurriculum] = useState(null);
  const [progress, setProgress]     = useState(null);
  const [activeNumber, setActiveNumber] = useState(params.get("m") || null);
  const [note, setNote] = useState("");
  const [noteStatus, setNoteStatus] = useState("idle"); // idle | saving | saved
  const [zoom, setZoom] = useState(false);
  const [exam, setExam] = useState(null);
  const [examAnswers, setExamAnswers] = useState({});
  const [examResult, setExamResult] = useState(null);
  const [examLoading, setExamLoading] = useState(false);
  const [err, setErr] = useState("");
  // Phase 19 — mobile-only second hamburger that opens the module tree
  const [moduleDrawerOpen, setModuleDrawerOpen] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([
        api.get("/training/v2/curriculum"),
        api.get("/training/v2/progress"),
      ]);
      setCurriculum(c.data);
      setProgress(p.data);
      if (!activeNumber && c.data.modules?.length) {
        setActiveNumber(c.data.modules[0].number);
      }
    } catch (e) { setErr(apiErrorMessage(e)); }
  }, [activeNumber]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load note for active module
  useEffect(() => {
    if (!activeNumber) return;
    setNote(""); setNoteStatus("idle");
    api.get(`/training/v2/notes/${activeNumber}`)
      .then(({ data }) => setNote(data.body || ""))
      .catch(() => {});
  }, [activeNumber]);

  // Autosave note (debounced)
  useEffect(() => {
    if (!activeNumber) return;
    const t = setTimeout(async () => {
      setNoteStatus("saving");
      try {
        await api.put(`/training/v2/notes/${activeNumber}`, { body: note });
        setNoteStatus("saved");
      } catch (e) { setNoteStatus("idle"); }  // eslint-disable-line no-unused-vars
    }, 800);
    return () => clearTimeout(t);
  }, [note, activeNumber]);

  const modules = curriculum?.modules || [];
  const activeIdx = modules.findIndex((m) => m.number === activeNumber);
  const active = modules[activeIdx];
  const completedSet = useMemo(() => new Set(progress?.completed || []), [progress]);
  const isCompleted = completedSet.has(activeNumber);

  const goto = (number) => {
    setActiveNumber(number);
    setParams({ m: number });
    // Phase 19 — close the mobile module drawer when a module is selected
    setModuleDrawerOpen(false);
  };
  const prev = () => activeIdx > 0 && goto(modules[activeIdx - 1].number);
  const next = () => activeIdx < modules.length - 1 && goto(modules[activeIdx + 1].number);

  const toggleComplete = async () => {
    try {
      await api.put(`/training/v2/progress/${activeNumber}`, { completed: !isCompleted });
      await loadAll();
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  // ---------------- Certification ----------------
  const startExamAs = async (role) => {
    setExamLoading(true); setExamResult(null); setExamAnswers({});
    try {
      const { data } = await api.get(`/training/v2/certification/${role}/start`);
      setExam(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setExamLoading(false); }
  };
  const startExam = () => progress && startExamAs(progress.role);

  const submitExam = async () => {
    if (!exam) return;
    setExamLoading(true);
    try {
      const { data } = await api.post("/training/v2/certification/submit", {
        exam_token: exam.exam_token,
        role: exam.role,
        answers: exam.questions.map((q) => ({
          question_id: q.id, choice: examAnswers[q.id] ?? -1,
        })),
      });
      setExamResult(data);
      await loadAll();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setExamLoading(false); }
  };

  // Group modules by parent (1 / 1.1, 1.2 …) for the left tree
  const tree = useMemo(() => {
    const map = {};
    for (const m of modules) {
      const parent = m.parent || m.number;
      if (!map[parent]) map[parent] = { master: null, children: [] };
      if (m.number === parent) map[parent].master = m;
      else map[parent].children.push(m);
    }
    return Object.values(map);
  }, [modules]);

  if (err) return <div className="max-w-7xl mx-auto px-4 py-10 text-rose-300" data-testid="training-error">{err}</div>;
  if (!curriculum) return <div className="max-w-7xl mx-auto px-4 py-10 text-sm text-lifted-dim">Loading training…</div>;
  if (modules.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10" data-testid="training-empty">
        <div className="glass-panel p-8 text-center">
          <BookOpen className="h-8 w-8 mx-auto text-lifted-green mb-2" />
          <h1 className="font-anton text-2xl uppercase">No curriculum assigned</h1>
          <p className="text-sm text-lifted-dim mt-2">Your role does not have an active training curriculum yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6" data-testid="training-experience">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="eyebrow">Lifted Training · {curriculum.role}</div>
          <h1 className="font-anton text-2xl sm:text-3xl uppercase truncate">
            {active?.title || "Training Experience"}
          </h1>
        </div>
        {/* Phase 19 — mobile-only secondary hamburger (lime/lifted-green
            accent) that opens the module tree as a sheet so the modules
            don't push the player off the screen. Hidden on desktop. */}
        <button
          type="button"
          onClick={() => setModuleDrawerOpen(true)}
          className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-lifted-green/50 bg-lifted-green/15 text-lifted-green font-bebas tracking-[0.18em] uppercase text-xs"
          data-testid="training-modules-hamburger"
          aria-label="Open training modules"
        >
          <Menu className="h-4 w-4" /> Training Modules
        </button>
      </header>

      {/* Phase 19 — mobile module sheet (drawer) */}
      {moduleDrawerOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/75 backdrop-blur-sm"
          onClick={() => setModuleDrawerOpen(false)}
          data-testid="training-modules-drawer"
        >
          <div
            className="absolute right-0 top-0 h-full w-[85vw] max-w-sm bg-black/95 border-l border-lifted-green/30 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="font-bebas tracking-[0.22em] text-sm text-lifted-green uppercase">
                Training Modules
              </div>
              <button onClick={() => setModuleDrawerOpen(false)}
                      className="text-lifted-dim hover:text-white"
                      data-testid="training-modules-drawer-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {tree.map((grp, gi) => {
                const groupKey = grp.master?.number
                  || (grp.children.length ? `parent-${grp.children[0].parent || gi}` : `grp-${gi}`);
                return (
                  <div key={groupKey} className="mb-2">
                    {grp.master && (
                      <button onClick={() => goto(grp.master.number)}
                              className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 transition ${
                                activeNumber === grp.master.number
                                  ? "bg-lifted-green/15 text-lifted-green"
                                  : "text-lifted-text hover:bg-white/[0.05]"
                              }`}
                              data-testid={`tree-mobile-${grp.master.number}`}>
                        <span className="font-mono text-xs text-lifted-dim w-7">{grp.master.number}</span>
                        <span className="truncate flex-1">{grp.master.title}</span>
                        {completedSet.has(grp.master.number) && <CheckCircle2 className="h-4 w-4 text-lifted-green shrink-0" />}
                      </button>
                    )}
                    {grp.children.map((c) => (
                      <button key={c.number} onClick={() => goto(c.number)}
                              className={`w-full text-left pl-9 pr-3 py-1.5 rounded text-xs flex items-center gap-2 transition ${
                                activeNumber === c.number
                                  ? "bg-lifted-green/10 text-lifted-green"
                                  : "text-lifted-dim hover:text-white hover:bg-white/[0.04]"
                              }`}
                              data-testid={`tree-mobile-${c.number}`}>
                        <span className="font-mono w-9">{c.number}</span>
                        <span className="truncate flex-1">{c.title}</span>
                        {completedSet.has(c.number) && <CheckCircle2 className="h-3 w-3 text-lifted-green shrink-0" />}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-[230px_1fr_280px] gap-4 items-start">
        {/* LEFT NAV TREE — hidden on mobile (drawer takes over) */}
        <aside className="hidden lg:block glass-panel p-3 lg:sticky lg:top-4 max-h-[80vh] overflow-y-auto" data-testid="training-tree">
          <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase mb-2">Modules</div>
          {tree.map((grp, gi) => {
            const groupKey = grp.master?.number
              || (grp.children.length ? `parent-${grp.children[0].parent || gi}` : `grp-${gi}`);
            return (
            <div key={groupKey} className="mb-2">
              {grp.master && (
                <button onClick={() => goto(grp.master.number)}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-center gap-2 transition ${
                          activeNumber === grp.master.number ? "bg-lifted-green/15 text-lifted-green" : "text-lifted-text hover:bg-white/[0.05]"
                        }`}
                        data-testid={`tree-${grp.master.number}`}>
                  <span className="font-mono text-[0.65rem] text-lifted-dim w-6">{grp.master.number}</span>
                  <span className="truncate flex-1">{grp.master.title}</span>
                  {completedSet.has(grp.master.number) && <CheckCircle2 className="h-3 w-3 text-lifted-green shrink-0" />}
                </button>
              )}
              {grp.children.map((c) => (
                <button key={c.number} onClick={() => goto(c.number)}
                        className={`w-full text-left pl-7 pr-2 py-1 rounded text-[0.7rem] flex items-center gap-2 transition ${
                          activeNumber === c.number ? "bg-lifted-green/10 text-lifted-green" : "text-lifted-dim hover:text-white hover:bg-white/[0.04]"
                        }`}
                        data-testid={`tree-${c.number}`}>
                  <span className="font-mono w-8">{c.number}</span>
                  <span className="truncate flex-1">{c.title}</span>
                  {completedSet.has(c.number) && <CheckCircle2 className="h-3 w-3 text-lifted-green shrink-0" />}
                </button>
              ))}
            </div>
          );})}
        </aside>

        {/* CENTER — current slide */}
        <main className="space-y-4">
          {active && (
            <>
              {/* Hero image */}
              <div className="glass-panel overflow-hidden" data-testid="slide-hero">
                {active.hero_image ? (
                  <button onClick={() => setZoom(true)} className="relative w-full group block">
                    <img src={active.hero_image} alt={active.title}
                         className="w-full max-h-[520px] object-contain bg-black" />
                    <span className="absolute top-3 right-3 bg-black/60 text-white text-[0.65rem] px-2 py-1 rounded inline-flex items-center gap-1 opacity-80 group-hover:opacity-100">
                      <Maximize2 className="h-3 w-3" /> Zoom
                    </span>
                  </button>
                ) : (
                  <div className="aspect-[16/9] grid place-items-center text-lifted-dim text-xs">
                    No image for this module yet.
                  </div>
                )}
              </div>

              {/* Slide meta */}
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <div className="text-[0.65rem] font-mono text-lifted-dim">MODULE {active.number}</div>
                  <h2 className="font-anton text-2xl uppercase" data-testid="slide-title">{active.title}</h2>
                </div>
                <div className="flex gap-2 items-center">
                  <button onClick={prev} disabled={activeIdx === 0}
                          className="btn-ghost text-xs disabled:opacity-30 inline-flex items-center gap-1"
                          data-testid="slide-prev">
                    <ChevronLeft className="h-4 w-4" /> Previous
                  </button>
                  <button onClick={toggleComplete}
                          className={`btn-lifted !py-2 !px-3 text-xs inline-flex items-center gap-1.5 ${
                            isCompleted ? "!bg-lifted-green/20 !text-lifted-green" : ""
                          }`}
                          data-testid="slide-toggle-complete">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {isCompleted ? "Completed" : "Mark complete"}
                  </button>
                  <button onClick={next} disabled={activeIdx === modules.length - 1}
                          className="btn-ghost text-xs disabled:opacity-30 inline-flex items-center gap-1"
                          data-testid="slide-next">
                    Next <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="glass-panel p-4" data-testid="slide-notes">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase inline-flex items-center gap-1.5">
                    <FileText className="h-3 w-3 text-lifted-green" /> My notes
                  </div>
                  <span className="text-[0.65rem] text-lifted-dim">
                    {noteStatus === "saving" ? "Saving…" : noteStatus === "saved" ? "Saved ✓" : ""}
                  </span>
                </div>
                <textarea
                  value={note}
                  onChange={(e) => { setNote(e.target.value); setNoteStatus("idle"); }}
                  rows={5}
                  placeholder="Capture key takeaways, scripts, or reminders…"
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-lifted-green/40 focus:outline-none"
                  data-testid="slide-notes-input"
                />
                <div className="mt-2 text-[0.6rem] text-lifted-dim">
                  Private to your account · autosave on
                </div>
              </div>

              {/* Slide-number pager */}
              <div className="flex flex-wrap gap-1 text-[0.65rem]" data-testid="slide-pager">
                {modules.map((m, i) => (
                  <button key={m.number} onClick={() => goto(m.number)}
                          className={`w-9 h-7 rounded font-mono ${
                            m.number === activeNumber ? "bg-lifted-green text-black"
                              : completedSet.has(m.number) ? "bg-lifted-green/15 text-lifted-green"
                              : "bg-white/[0.04] text-lifted-dim hover:text-white"
                          }`}
                          data-testid={`pager-${m.number}`}
                          title={m.title}>
                    {m.number}
                  </button>
                ))}
              </div>
            </>
          )}
        </main>

        {/* RIGHT SIDEBAR — progress + certification */}
        <aside className="glass-panel p-4 space-y-4 lg:sticky lg:top-4" data-testid="training-progress">
          {progress?.role !== "master_admin" && (
            <>
              <div>
                <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase">Training progress</div>
                <div className="font-anton text-3xl mt-1 text-lifted-green" data-testid="progress-pct">{pct(progress?.pct ?? 0)}</div>
                <div className="text-[0.65rem] text-lifted-dim">
                  {progress?.completed?.length || 0} of {progress?.required?.length || 0} slides done
                </div>
                <div className="mt-2 h-1.5 bg-black/40 rounded-full overflow-hidden">
                  <div className="h-full bg-lifted-green transition-all" style={{ width: `${progress?.pct || 0}%` }} />
                </div>
              </div>

              <div>
                <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase mb-1">Status</div>
                <StatusChip status={progress?.status} />
              </div>

              {progress?.remaining?.length > 0 && (
                <div>
                  <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase mb-1">Next up</div>
                  <button onClick={() => goto(progress.remaining[0])}
                          className="text-xs text-lifted-text hover:text-lifted-green"
                          data-testid="progress-next-required">
                    Module {progress.remaining[0]} →
                  </button>
                </div>
              )}

              <div>
                <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase mb-1">Certification</div>
                {progress?.status === "certified" && progress?.certification ? (
                  <div className="text-xs space-y-0.5" data-testid="cert-status-passed">
                    <div className="text-lifted-green inline-flex items-center gap-1"><Award className="h-3.5 w-3.5" /> Certified {progress.role}</div>
                    <div className="text-lifted-dim font-mono">{progress.certification.certification_id}</div>
                    <div className="text-lifted-dim text-[0.6rem]">{progress.certification.created_at?.slice(0,10)} · {progress.certification.score}/{progress.certification.total}</div>
                  </div>
                ) : progress?.status === "ready_for_certification" ? (
                  <button onClick={startExam} disabled={examLoading}
                          className="btn-lifted !py-2 !px-3 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid="cert-start">
                    {examLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Award className="h-3.5 w-3.5" />}
                    Start certification
                  </button>
                ) : (
                  <div className="text-[0.65rem] text-lifted-dim inline-flex items-center gap-1.5" data-testid="cert-locked">
                    <Lock className="h-3 w-3" /> Complete all modules to unlock
                  </div>
                )}
              </div>
            </>
          )}
          {progress?.role === "master_admin" && (
            <>
              <div className="text-xs text-lifted-dim">
                You have full access to every training module and certification flow.
              </div>
              <div>
                <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase mb-2">Run certification</div>
                <div className="flex flex-wrap gap-1.5" data-testid="admin-cert-picker">
                  {["affiliate", "driver", "partner", "franchisee"].map((r) => (
                    <button key={r} onClick={() => startExamAs(r)} disabled={examLoading}
                            className="btn-lifted !py-1.5 !px-3 text-[0.7rem] inline-flex items-center gap-1.5 disabled:opacity-50"
                            data-testid={`cert-start-${r}`}>
                      <Award className="h-3 w-3" /> {r}
                    </button>
                  ))}
                </div>
              </div>
              {progress?.certifications?.length > 0 && (
                <div>
                  <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-dim uppercase mb-1">My certifications</div>
                  <ul className="space-y-1 text-[0.65rem]">
                    {progress.certifications.map((c) => (
                      <li key={c.id} className="text-lifted-text inline-flex items-center gap-1">
                        <Award className="h-3 w-3 text-lifted-green" /> {c.role} · {c.score}/{c.total} ·
                        <span className="text-lifted-dim font-mono">{c.certification_id}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </aside>
      </div>

      {/* Zoom modal */}
      {zoom && active?.hero_image && (
        <div className="fixed inset-0 z-50 bg-black/95 grid place-items-center p-4" onClick={() => setZoom(false)}
             data-testid="zoom-modal">
          <img src={active.hero_image} alt={active.title} className="max-w-full max-h-full object-contain" />
          <button onClick={() => setZoom(false)} className="absolute top-4 right-4 text-white">
            <X className="h-6 w-6" />
          </button>
        </div>
      )}

      {/* Exam modal */}
      {exam && (
        <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4 overflow-y-auto"
             data-testid="exam-modal">
          <div className="glass-panel max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto bg-lifted-bg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="eyebrow">Certification · {exam.role}</div>
                <h2 className="font-anton text-2xl uppercase">20 Questions · Pass 14/20</h2>
              </div>
              <button onClick={() => { setExam(null); setExamResult(null); }} className="text-lifted-dim hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            {!examResult ? (
              <>
                <ol className="space-y-4" data-testid="exam-questions">
                  {exam.questions.map((q, i) => (
                    <li key={q.id} className="glass-panel p-3" data-testid={`exam-q-${i+1}`}>
                      <div className="text-xs text-lifted-dim mb-1">Q{i+1} · module {q.module}</div>
                      <div className="text-sm text-lifted-text mb-2">{q.q}</div>
                      <div className="grid sm:grid-cols-2 gap-1">
                        {q.choices.map((c, j) => (
                          <label key={j} className={`block px-2 py-1.5 rounded text-xs cursor-pointer border transition ${
                            examAnswers[q.id] === j ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40"
                              : "border-white/5 text-lifted-text hover:border-white/20"
                          }`}>
                            <input type="radio" name={q.id} checked={examAnswers[q.id] === j}
                                   onChange={() => setExamAnswers((p) => ({ ...p, [q.id]: j }))}
                                   className="sr-only"
                                   data-testid={`exam-q-${i+1}-choice-${j}`} />
                            {String.fromCharCode(65 + j)}. {c}
                          </label>
                        ))}
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="mt-4 flex justify-end">
                  <button onClick={submitExam} disabled={examLoading}
                          className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid="exam-submit">
                    {examLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                    Submit exam
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-6" data-testid="exam-result">
                {examResult.passed ? (
                  <>
                    <Award className="h-12 w-12 text-lifted-green mx-auto mb-2" />
                    <h3 className="font-anton text-3xl uppercase text-lifted-green">Certified!</h3>
                    <p className="text-sm text-lifted-text mt-1">
                      You scored {examResult.score}/{examResult.total} · pass mark {examResult.pass_score}.
                    </p>
                    <div className="font-mono text-xs text-lifted-dim mt-3">{examResult.certification?.certification_id}</div>
                  </>
                ) : (
                  <>
                    <X className="h-12 w-12 text-rose-300 mx-auto mb-2" />
                    <h3 className="font-anton text-3xl uppercase text-rose-200">Not passed yet</h3>
                    <p className="text-sm text-lifted-text mt-1">
                      You scored {examResult.score}/{examResult.total} · pass mark {examResult.pass_score}. Review your modules and retake.
                    </p>
                  </>
                )}
                <button onClick={() => { setExam(null); setExamResult(null); }} className="btn-lifted !py-2 !px-4 text-xs mt-4">
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const config = {
    not_started:             { label: "Not started",            cls: "bg-white/5 text-lifted-dim border-white/10" },
    in_progress:             { label: "In progress",            cls: "bg-amber-400/15 text-amber-200 border-amber-400/30" },
    ready_for_certification: { label: "Ready for certification", cls: "bg-sky-500/15 text-sky-200 border-sky-500/30" },
    certified:               { label: "Certified",              cls: "bg-lifted-green/15 text-lifted-green border-lifted-green/30" },
  }[status] || { label: status || "—", cls: "border-white/10 text-lifted-dim" };
  return (
    <span className={`inline-flex items-center gap-1 text-[0.6rem] font-bebas uppercase tracking-[0.18em] border rounded-full px-2 py-0.5 ${config.cls}`}>
      {config.label}
    </span>
  );
}
